// Serial video-ingest running context (§10.2 rewrite). Pure & deterministic —
// no LLM. As episodes are parsed one at a time, this carries a global entity
// registry (canonical + provisional identities with visual anchors, plot role,
// known outfit states) plus a compacting plot digest and open story threads.
// renderContext() serializes it into the markdown block fed back into the next
// episode's parse so the model resolves identity by plot — not by re-matching
// faces in isolation. identitySeed stays fixed while direct naming evidence may
// upgrade the current canonical name; previous names accumulate as aliases for
// the deterministic finalize sweep (buildAliasMap → renameDocs).

import type { SerialIdentityProfile, SerialOutput, SerialStateDescription } from "./video-serial-md.js";
import type { VideoAnchor, VideoEpisodeDoc, VideoScene } from "./video-records.js";
import { isCrowdName } from "./serial-context.js";

export type EntityKind = "actor" | "location" | "prop";

export interface RegistryState {
  name: string;
  description: string;
}

export interface RegistryEntity {
  identitySeed: string; // immutable first-seen name used by stable Script v4 identity
  name: string; // current canonical display name
  kind: EntityKind;
  status: "canonical" | "provisional";
  importance: "featured" | "background";
  recognitionAnchors: string[]; // discriminative stable cues for cross-episode re-identification
  identityProfile?: SerialIdentityProfile; // actor-only stable physical and voice traits
  appearance?: string; // location/prop visible form
  role: string; // plot identity / relationships
  aliases: string[]; // other surface names seen
  states: RegistryState[]; // known durable visual states — reused across episodes to stop drift
  firstEpisode: number;
  lastEpisode: number;
}

const EMPTY_IDENTITY_PROFILE = (): SerialIdentityProfile => ({
  apparent_gender: "",
  apparent_age: "",
  face_shape: "",
  brows_eyes: "",
  nose: "",
  mouth: "",
  skin_features: "",
  hair: "",
  build: "",
  persistent_marks: [],
  voice: "",
});

export interface EpisodeDigest {
  episode: number;
  synopsis: string;
  outcome?: "parsed" | "skipped";
}

export interface OpenThread {
  id: string;
  text: string;
  sinceEpisode: number;
}

export interface RunContext {
  lastEpisode: number;
  registry: RegistryEntity[];
  digests: EpisodeDigest[];
  openThreads: OpenThread[];
}

export function emptyContext(): RunContext {
  return { lastEpisode: 0, registry: [], digests: [], openThreads: [] };
}

const norm = (s: string): string => s.normalize("NFC").replace(/\s+/g, " ").trim();
const clip = (s: string, n: number): string => (s.length > n ? `${s.slice(0, n)}…` : s);

function findRegistryEntity(registry: RegistryEntity[], name: string): RegistryEntity | undefined {
  const key = norm(name);
  return registry.find((entity) =>
    norm(entity.name) === key
    || norm(entity.identitySeed) === key
    || entity.aliases.some((alias) => norm(alias) === key));
}

const RECENT_N = 3; // recent episodes kept with full synopsis
const CHAR_BUDGET = 12000; // renderContext output ceiling; shrink the window to fit

function pushUniq(arr: string[], v?: string): void {
  const s = (v ?? "").trim();
  if (s && !arr.includes(s)) arr.push(s);
}

const PROFILE_TEXT_KEYS = [
  "apparent_gender",
  "apparent_age",
  "face_shape",
  "brows_eyes",
  "nose",
  "mouth",
  "skin_features",
  "hair",
  "build",
  "voice",
] as const;

function mergeIdentityProfile(
  current: SerialIdentityProfile | undefined,
  patch: Partial<SerialIdentityProfile> | undefined,
  overwrite: boolean,
): SerialIdentityProfile | undefined {
  if (!patch) return current;
  const profile = current ? { ...current, persistent_marks: [...current.persistent_marks] } : EMPTY_IDENTITY_PROFILE();
  for (const key of PROFILE_TEXT_KEYS) {
    const value = patch[key]?.trim() ?? "";
    if (value && (overwrite || !profile[key])) profile[key] = value;
  }
  for (const mark of patch.persistent_marks ?? []) pushUniq(profile.persistent_marks, mark);
  return profile;
}

function upsertState(states: RegistryState[], incoming: SerialStateDescription, overwrite: boolean): void {
  const name = incoming.name.trim();
  if (!name) return;
  const description = incoming.description.trim();
  const existing = states.find((state) => norm(state.name) === norm(name));
  if (!existing) {
    states.push({ name, description });
    return;
  }
  if (description && (overwrite || !existing.description)) existing.description = description;
}

// Merge fresh evidence into an existing registry entity: unions recognition
// anchors, aliases, and states while patching identity-profile fields independently.
// `overwrite` controls scalar identity-profile fields plus appearance/role:
//   - false (a re-registration of a known name via 新增): fill empties only, never
//     clobber what we already know.
//   - true (an explicit 修正 correction): the model deliberately looked again and
//     gives a better value → replace even a non-empty field.
function enrichEntity(
  e: RegistryEntity,
  d: {
    add_recognition_anchors?: string[];
    identity_profile_patch?: Partial<SerialIdentityProfile>;
    appearance?: string;
    role?: string;
    add_aliases?: string[];
    add_states?: SerialStateDescription[];
  },
  episode: number,
  overwrite = false,
): void {
  const set = (has: string | undefined, val: string | undefined): string | undefined => {
    const v = (val ?? "").trim();
    if (!v) return has;
    return overwrite || !has ? v : has;
  };
  for (const anchor of d.add_recognition_anchors ?? []) pushUniq(e.recognitionAnchors, anchor);
  e.identityProfile = mergeIdentityProfile(e.identityProfile, d.identity_profile_patch, overwrite);
  const appearance = set(e.appearance, d.appearance);
  if (appearance) e.appearance = appearance;
  e.role = set(e.role, d.role) ?? e.role;
  for (const a of d.add_aliases ?? []) pushUniq(e.aliases, a);
  for (const state of d.add_states ?? []) upsertState(e.states, state, overwrite);
  e.lastEpisode = episode;
}

// Fold one episode's serial output into the context. Mutates and returns ctx
// (called once per episode in the serial loop).
export function applyDelta(ctx: RunContext, out: SerialOutput, episode: number): RunContext {
  const find = (name: string): RegistryEntity | undefined => findRegistryEntity(ctx.registry, name);

  for (const ne of out.registry_delta.new_entities) {
    const name = ne.name.trim();
    if (!name) continue;
    if (ne.kind === "actor" && isCrowdName(name)) continue;
    const existing = find(name);
    if (existing) {
      enrichEntity(existing, {
        add_recognition_anchors: ne.recognition_anchors,
        identity_profile_patch: ne.identity_profile,
        appearance: ne.appearance,
        role: ne.role,
        add_aliases: ne.aliases,
        add_states: ne.states,
      }, episode);
      continue;
    }
    const states: RegistryState[] = [];
    for (const state of ne.states ?? []) upsertState(states, state, false);
    const aliases: string[] = [];
    for (const a of ne.aliases ?? []) pushUniq(aliases, a);
    ctx.registry.push({
      identitySeed: name,
      name,
      kind: ne.kind,
      status: ne.status,
      importance: ne.importance,
      recognitionAnchors: [...new Set((ne.recognition_anchors ?? []).map((anchor) => anchor.trim()).filter(Boolean))],
      ...(ne.identity_profile ? { identityProfile: mergeIdentityProfile(undefined, ne.identity_profile, true) } : {}),
      ...(ne.appearance ? { appearance: ne.appearance.trim() } : {}),
      role: (ne.role ?? "").trim(),
      aliases,
      states,
      firstEpisode: episode,
      lastEpisode: episode,
    });
  }

  // enrich = the 修正 section: an explicit correction of a known entity → overwrite.
  for (const en of out.registry_delta.enrich) {
    const e = find(en.name);
    if (!e) continue;
    enrichEntity(e, en, episode, true);
  }

  for (const change of out.registry_delta.canonicalize) {
    const source = find(change.from_name);
    const canonicalName = change.canonical_name.trim();
    if (!source || !canonicalName || norm(canonicalName) === norm(source.name)) continue;
    const target = find(canonicalName);

    if (target && target !== source) {
      // Preserve the identity that entered the story first; on a tie, prefer the
      // already-canonical target. Display metadata comes from the named target,
      // while lists and appearance history are combined without loss.
      const stable = source.firstEpisode < target.firstEpisode ? source : target;
      const other = stable === source ? target : source;
      const stableStates = stable.states.map((state) => ({ ...state }));
      const aliases = new Set([
        ...source.aliases,
        ...target.aliases,
        source.identitySeed,
        target.identitySeed,
        source.name,
        target.name,
        change.from_name,
      ].map((name) => name.trim()).filter(Boolean));

      stable.name = canonicalName;
      stable.status = "canonical";
      stable.importance = source.importance === "featured" || target.importance === "featured" ? "featured" : "background";
      stable.recognitionAnchors = [];
      for (const anchor of [...source.recognitionAnchors, ...target.recognitionAnchors]) pushUniq(stable.recognitionAnchors, anchor);
      const sourceProfile = mergeIdentityProfile(undefined, source.identityProfile, true);
      stable.identityProfile = mergeIdentityProfile(sourceProfile, target.identityProfile, true);
      if (target.appearance || source.appearance) stable.appearance = target.appearance || source.appearance;
      stable.role = target.role || source.role;
      stable.states = [];
      for (const state of [...stableStates, ...other.states]) upsertState(stable.states, state, false);
      stable.aliases = [...aliases].filter((alias) => norm(alias) !== norm(canonicalName));
      stable.firstEpisode = Math.min(source.firstEpisode, target.firstEpisode);
      stable.lastEpisode = Math.max(source.lastEpisode, target.lastEpisode, episode);
      ctx.registry.splice(ctx.registry.indexOf(other), 1);
      continue;
    }

    pushUniq(source.aliases, source.name);
    pushUniq(source.aliases, change.from_name);
    source.name = canonicalName;
    source.status = "canonical";
    source.aliases = source.aliases.filter((alias) => norm(alias) !== norm(canonicalName));
    source.lastEpisode = episode;
  }

  let seq = 0;
  for (const op of out.open_threads.opened) {
    const text = op.text.trim();
    if (text) ctx.openThreads.push({ id: `t${episode}_${++seq}`, text, sinceEpisode: episode });
  }
  for (const cl of out.open_threads.closed) {
    const ref = cl.ref.trim();
    if (ref) ctx.openThreads = ctx.openThreads.filter((t) => t.id !== ref && t.text !== ref);
  }

  ctx.digests.push({ episode, synopsis: (out.synopsis ?? "").trim() });
  ctx.lastEpisode = episode;
  return ctx;
}

export function advanceSkippedEpisode(ctx: RunContext, episode: number): RunContext {
  ctx.digests.push({ episode, synopsis: "", outcome: "skipped" });
  ctx.lastEpisode = episode;
  return ctx;
}

// ===== renderContext (compaction folded in) =====

function renderActor(e: RegistryEntity, hard: boolean): string[] {
  const L: string[] = [];
  const tag = e.status === "provisional" ? "·待定 ⚠" : `·${e.importance}`;
  L.push(`### ${e.name}  ${tag}`);
  if (e.role) L.push(`- 作用: ${clip(e.role, hard ? 40 : 200)}`);
  if (e.recognitionAnchors.length) L.push(`- 识别锚点: ${e.recognitionAnchors.join("；")}`);
  const profile = e.identityProfile;
  if (profile) {
    const ageGender = [profile.apparent_gender, profile.apparent_age].filter(Boolean).join("，");
    if (ageGender) L.push(`- 年龄性别: ${ageGender}`);
    if (profile.face_shape) L.push(`- 脸型下颌: ${profile.face_shape}`);
    if (profile.brows_eyes) L.push(`- 眉眼: ${profile.brows_eyes}`);
    if (profile.nose) L.push(`- 鼻部: ${profile.nose}`);
    if (profile.mouth) L.push(`- 唇形嘴角: ${profile.mouth}`);
    if (profile.skin_features) L.push(`- 肤色面部: ${profile.skin_features}`);
    if (profile.hair) L.push(`- 发型: ${profile.hair}`);
    if (profile.build) L.push(`- 体态: ${profile.build}`);
    if (profile.persistent_marks.length) L.push(`- 固定标记: ${profile.persistent_marks.join("；")}`);
    if (profile.voice) L.push(`- 声线: ${profile.voice}`);
  }
  if (e.aliases.length) L.push(`- 曾用称呼: ${e.aliases.join("、")}`);
  if (e.states.length) {
    L.push(`- 已知造型: ${e.states.map((state) => {
      const description = clip(state.description, hard ? 30 : 90);
      return description ? `${state.name}（${description}）` : state.name;
    }).join(" / ")}`);
  }
  L.push(`- 出现: ep ${e.firstEpisode}–${e.lastEpisode}`);
  if (e.status === "provisional") L.push(`- 身份未确认：本集若出现书面姓名或台词本名，用 canonicalize 从「${e.name}」升级规范名`);
  return L;
}

function renderAsset(e: RegistryEntity, hard: boolean): string {
  const desc = clip(e.appearance || e.role || "", hard ? 30 : 60);
  const states = e.states.map((state) => state.description ? `${state.name}（${clip(state.description, hard ? 20 : 60)}）` : state.name).join(" / ");
  return `- ${e.name} ·${e.importance}${desc ? `：${desc}` : ""}${states ? `；状态：${states}` : ""}`;
}

function render(ctx: RunContext, recentN: number, hard: boolean): string {
  const L: string[] = [];
  L.push(`# 已知设定（解析第 ${ctx.lastEpisode + 1} 集时对照本表）`, "");
  L.push("认得出的人/物沿用其**规范名**，别自己新起名；人物优先比对识别锚点与结构化身份画像，服装只作弱辅助。", "");

  const actors = ctx.registry.filter((e) => e.kind === "actor");
  const locations = ctx.registry.filter((e) => e.kind === "location");
  const props = ctx.registry.filter((e) => e.kind === "prop");

  if (actors.length) {
    L.push("## 人物", "");
    for (const e of actors) L.push(...renderActor(e, hard), "");
  }
  if (locations.length || props.length) {
    L.push("## 场景 / 道具", "");
    for (const e of locations) L.push(renderAsset(e, hard));
    for (const e of props) L.push(renderAsset(e, hard));
    L.push("");
  }

  const recentDigests = ctx.digests.filter((digest) => digest.episode > ctx.lastEpisode - recentN);
  if (recentDigests.length) {
    L.push("## 剧情进展", "");
    for (const d of [...recentDigests].sort((a, b) => a.episode - b.episode)) {
      L.push(d.outcome === "skipped"
        ? `第${d.episode}集已人工跳过，剧情未知。`
        : `第${d.episode}集：${hard ? clip(d.synopsis, 40) : d.synopsis}`);
    }
    L.push("");
  }

  if (ctx.openThreads.length) {
    L.push("## 未闭合线索（若本集交代了，用 open_threads.closed 引用其 [id] 关闭）", "");
    for (const t of ctx.openThreads) L.push(`- [${t.id}] ${t.text}`);
    L.push("");
  }

  return L.join("\n");
}

// Serialize the context for the next episode's prompt. Registry + open threads
// are load-bearing identity anchors and are NEVER compacted. Plot history is a
// rolling window: older resolved events would otherwise grow without bound and
// compete with the current episode's audio/visual evidence.
export function renderContext(ctx: RunContext): string {
  for (let n = RECENT_N; n >= 1; n--) {
    const out = render(ctx, n, false);
    if (out.length <= CHAR_BUDGET) return out;
  }
  return render(ctx, 1, true); // n==1 still over budget → hard-trim digests/appearance
}

// ===== historical surface name → current canonical name (finalize) =====

export function buildAliasMap(registry: RegistryEntity[]): Map<string, string> {
  const aliases = new Map<string, string>();
  for (const entity of registry) {
    for (const surface of [entity.identitySeed, ...entity.aliases]) {
      if (norm(surface) !== norm(entity.name)) aliases.set(surface, entity.name);
    }
  }
  return aliases;
}

// ===== registry metadata stamping =====

type LooseScript = { actors?: Asset[]; locations?: Asset[]; props?: Asset[] };
type AssetState = { state_name: string; description: string };
type Asset = {
  actor_name?: string;
  location_name?: string;
  prop_name?: string;
  aliases?: string[];
  importance?: string;
  description?: string;
  states?: AssetState[];
};

function actorAssetDescription(entity: RegistryEntity): string {
  const profile = entity.identityProfile;
  const parts: string[] = [];
  if (profile) {
    const ageGender = [profile.apparent_gender, profile.apparent_age].filter(Boolean).join("，");
    if (ageGender) parts.push(`外观年龄与性别：${ageGender}`);
    if (profile.face_shape) parts.push(`脸型下颌：${profile.face_shape}`);
    if (profile.brows_eyes) parts.push(`眉眼：${profile.brows_eyes}`);
    if (profile.nose) parts.push(`鼻部：${profile.nose}`);
    if (profile.mouth) parts.push(`唇形口周：${profile.mouth}`);
    if (profile.skin_features) parts.push(`肤色面部：${profile.skin_features}`);
    if (profile.hair) parts.push(`头发：${profile.hair}`);
    if (profile.build) parts.push(`骨架体型：${profile.build}`);
    if (profile.persistent_marks.length) parts.push(`固定标记：${profile.persistent_marks.join("、")}`);
    if (profile.voice) parts.push(`声线：${profile.voice}`);
  }
  if (entity.recognitionAnchors.length) parts.push(`识别锚点：${entity.recognitionAnchors.join("；")}`);
  if (entity.role) parts.push(`角色语境：${entity.role}`);
  return parts.map((part) => part.replace(/[。；]+$/, "")).join("；");
}

// The video path bypasses runGovern (which used to inject asset metadata), so
// stamp descriptions, state descriptions, importance, and aliases deterministically
// from the registry before governDeterministic.
// Importance: unmatched assets default to "featured" (never silently hidden),
// matching apply-plan. Historical names from identitySeed + aliases are folded
// onto the canonical asset (renameDocs collapsed them to one name, so
// assignProvisionalIds saw no aliases).
export function stampRegistryMetadata<T extends LooseScript>(script: T, registry: RegistryEntity[]): T {
  const imp = new Map<string, "featured" | "background">();
  const surfaceFor = new Map<string, Set<string>>(); // canonical norm name → surface aliases
  const addSurface = (canon: string, alias: string): void => {
    const k = norm(canon);
    const a = alias.trim();
    if (!a || norm(a) === k) return;
    if (!surfaceFor.has(k)) surfaceFor.set(k, new Set());
    surfaceFor.get(k)!.add(a);
  };
  for (const e of registry) {
    imp.set(norm(e.name), e.importance);
    for (const a of e.aliases) imp.set(norm(a), e.importance);
    imp.set(norm(e.identitySeed), e.importance);
    for (const alias of new Set([e.identitySeed, ...e.aliases])) addSurface(e.name, alias);
  }
  const stamp = (list: Asset[] | undefined, nameKey: keyof Asset): void => {
    for (const a of list ?? []) {
      const canonName = String(a[nameKey] ?? "");
      const names = [canonName, ...(a.aliases ?? [])];
      let entity: RegistryEntity | undefined;
      for (const nm of names) {
        entity = registry.find((candidate) =>
          norm(candidate.name) === norm(nm)
          || norm(candidate.identitySeed) === norm(nm)
          || candidate.aliases.some((alias) => norm(alias) === norm(nm)));
        if (entity) break;
      }
      a.importance = entity?.importance ?? imp.get(norm(canonName)) ?? "featured";
      const surf = surfaceFor.get(norm(canonName));
      if (surf?.size) a.aliases = [...new Set([...(a.aliases ?? []), ...surf])];
      if (!entity) continue;
      const description = entity.kind === "actor"
        ? actorAssetDescription(entity)
        : [entity.appearance, entity.role]
          .map((part) => (part?.trim() ?? "").replace(/[。；]+$/, ""))
          .filter(Boolean)
          .join("；");
      if (description) a.description = `${description}。`;
      for (const state of a.states ?? []) {
        const registryState = entity.states.find((candidate) => norm(candidate.name) === norm(state.state_name));
        if (registryState?.description) state.description = registryState.description;
        else if (state.state_name === "default" && entity.kind !== "actor" && entity.appearance) state.description = entity.appearance;
      }
    }
  };
  stamp(script.actors, "actor_name");
  stamp(script.locations, "location_name");
  stamp(script.props, "prop_name");
  return script;
}

// ===== serial output → per-episode working doc =====

// Map the serial parse output onto the established VideoEpisodeDoc shape. Only
// scenes + synopsis are populated (docsToNameScript consumes just those); the
// roster arrays stay empty since identity now lives in the running context.
export function deltaToDoc(out: SerialOutput, episode: number): VideoEpisodeDoc {
  const scenes: VideoScene[] = out.scenes.map((s, i) => {
    const env: { space?: string; time?: string } = {};
    if (s.environment?.space) env.space = s.environment.space;
    if (s.environment?.time) env.time = s.environment.time;
    const anchors: VideoAnchor[] = (s.anchors ?? []).map((a) => ({
      kind: a.kind,
      ...(a.speaker ? { speaker: a.speaker } : {}),
      ...(a.parenthetical ? { parenthetical: a.parenthetical } : {}),
      ...(a.ts ? { ts: a.ts } : {}),
      content: a.content ?? "",
    }));
    return {
      index: i + 1,
      environment: env,
      ...(s.timeRange ? { timeRange: s.timeRange } : {}),
      ...(s.location ? { location: s.location } : {}),
      characters: (s.characters ?? []).filter((c) => c.name).map((c) => ({ name: c.name, states: [...c.states] })),
      props: (s.props ?? []).filter(Boolean),
      anchors,
    };
  });
  return {
    episode,
    synopsis: (out.synopsis ?? "").trim(),
    scenes,
    actors: [],
    locations: [],
    props: [],
    speakers: [],
    actorStates: [],
    locationStates: [],
    propStates: [],
  };
}
